import React, { useState, useEffect, useMemo } from 'react';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import {
  Calendar, Clock, PawPrint, Send, CheckCircle, AlertCircle, XCircle,
  Video, MapPin, Car, Plane, DollarSign, ChevronRight, Info, Shield,
  Phone, FileText, ClipboardList, ArrowRight, Star, X
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== PRICING DATA ====================

const VIRTUAL_SESSIONS = [
  {
    value: 'puppy_consultation',
    label: 'Puppy Consultation',
    duration: '~60 min',
    price: 250,
    desc: 'First step into Edu Paws for new puppy owners. Understanding your puppy\'s True Nature.',
    note: 'Dogs under 8 months only',
    maxAge: 8,
  },
  {
    value: 'follow_up',
    label: 'Follow-Up Session',
    duration: '~45 min',
    price: 250,
    desc: 'Continue from a previous session. No new discovery, focused on implementation.',
    note: 'Existing clients only',
  },
  {
    value: 'one_on_one',
    label: '1-on-1 Training',
    duration: '~60 min',
    price: 500,
    desc: 'Personalized training session addressing specific behaviors and goals.',
  },
];

const LOCAL_SESSIONS = [
  {
    value: 'follow_up',
    label: 'Follow-Up Session',
    duration: '~60 min',
    price: 350,
    desc: 'In-home continuation from a previous session.',
    note: 'Existing clients only',
  },
  {
    value: 'puppy_consultation',
    label: 'Puppy Consultation',
    duration: '~60 min',
    price: 500,
    desc: 'In-home session for new puppy owners. Meet your puppy in their environment.',
    note: 'Dogs under 8 months only',
    maxAge: 8,
  },
  {
    value: 'one_on_one',
    label: '1-on-1 Training',
    duration: '~90 min',
    price: 750,
    desc: 'Comprehensive in-home training session with hands-on guidance.',
  },
  {
    value: 'behavior_assessment',
    label: 'Behavior Assessment Program',
    duration: 'Multi-session program',
    price: 2500,
    desc: 'The flagship program. In-home assessment, written plan, and implementation sessions.',
    isProgram: true,
    isInquiry: true,
  },
];

const TRAVEL_SESSIONS = [
  {
    value: 'day_rate',
    label: 'Day Rate',
    duration: '~4 hours on site',
    price: 2500,
    desc: 'Approximately 4 hours on site in two working blocks with rest between. One household.',
    note: 'Per day',
  },
  {
    value: 'two_day_intensive',
    label: 'Two-Day Intensive',
    duration: '2 consecutive days, ~4 hrs each',
    price: 4500,
    desc: 'Two consecutive days of training. Approximately 4 hours each, split into blocks.',
  },
  {
    value: 'behavior_assessment_travel',
    label: 'Behavior Assessment Program',
    duration: 'Multi-step program',
    price: 6500,
    desc: 'Full program adapted for distance. Two days on site plus virtual follow-through.',
    isProgram: true,
    isInquiry: true,
    note: 'Plus travel expenses',
  },
];

const BEHAVIOR_ASSESSMENT_TIERS = [
  {
    tier: 'local',
    label: 'Local',
    range: 'Within 25 miles of Santa Clarita',
    price: 2500,
    steps: [
      'Intake: Dog Psychology Quiz, written history form, phone call (~30 min)',
      'In-home assessment (~2 hours)',
      'Written plan delivered within 5 business days',
      'Two implementation sessions (~90 min each) across ~4 weeks',
      'Support window through final session (~5 weeks)',
    ],
    scope: '~5 hours in-person across ~5 weeks',
  },
  {
    tier: 'extended',
    label: 'Extended',
    range: '25 to 60 miles from Santa Clarita',
    price: 2950,
    steps: [
      'Same scope as Local program',
      '$150 travel fee applied across three in-person visits',
      'Intake: Dog Psychology Quiz, written history form, phone call (~30 min)',
      'In-home assessment (~2 hours)',
      'Written plan delivered within 5 business days',
      'Two implementation sessions (~90 min each) across ~4 weeks',
      'Support window through final session (~5 weeks)',
    ],
    scope: '~5 hours in-person across ~5 weeks',
  },
  {
    tier: 'travel',
    label: 'Travel',
    range: 'Beyond 60 miles / Out of state',
    price: 6500,
    steps: [
      'Intake: Dog Psychology Quiz, written history form, phone call (~60 min)',
      'Two consecutive days on site (~4 hours each, two blocks per day)',
      'Written plan delivered within 5 business days',
      'Three virtual implementation sessions (~60 min each) across ~6 weeks',
      'Support window from arrival through final virtual session',
    ],
    scope: '~8 hours in-person across 2 days, plus virtual follow-through',
    note: 'Travel expenses billed separately. 50% deposit at booking, non-refundable within 14 days of travel.',
  },
];

const STATUS_MAP = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

const FORMAT_TABS = [
  { id: 'virtual', label: 'Virtual', icon: Video, desc: 'Video session. Available anywhere.' },
  { id: 'local', label: 'In Person, Local', icon: MapPin, desc: 'Within 25 miles of Santa Clarita.' },
  { id: 'travel', label: 'In Person, Travel', icon: Plane, desc: 'Beyond 60 miles, including out of state.' },
];

// ==================== SUB COMPONENTS ====================

function SessionCard({ session, selected, onSelect, format }) {
  const isSelected = selected?.value === session.value;
  return (
    <button
      type="button"
      data-testid={`session-card-${session.value}`}
      onClick={() => onSelect(session)}
      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
        isSelected
          ? 'border-edupaws-sage bg-edupaws-sage/5 ring-1 ring-edupaws-sage/20'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-body font-bold text-sm text-edupaws-navy">{session.label}</div>
          <div className="text-xs text-gray-500 font-body mt-0.5">{session.desc}</div>
          <div className="flex items-center gap-3 mt-2">
            <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-body">
              <Clock className="w-3 h-3" /> {session.duration}
            </span>
            {session.note && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-body font-medium">
                <Info className="w-3 h-3" /> {session.note}
              </span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-heading font-bold text-lg text-edupaws-navy">${session.price.toLocaleString()}</div>
          {session.isProgram && <div className="text-[10px] text-gray-400 font-body">program</div>}
        </div>
      </div>
    </button>
  );
}

function BehaviorAssessmentDetail({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-edupaws-navy flex items-center gap-2">
            <Shield className="w-5 h-5 text-edupaws-sage" />
            Behavior Assessment Program
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 pt-2">
          <p className="text-sm text-gray-600 font-body leading-relaxed">
            The flagship program from Stephanie at the Dog Psychology Center. A multi-step assessment
            using the True Nature Framework. A structured program with
            intake, assessment, a written plan, and implementation sessions with ongoing support.
          </p>

          {/* How it works */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-body font-bold text-sm text-edupaws-navy mb-2">Required Intake (All Versions)</h4>
            <div className="space-y-2">
              {[
                { icon: ClipboardList, text: 'Dog Psychology Quiz completed online' },
                { icon: FileText, text: 'Written history form: age, household, behaviors, prior training' },
                { icon: Phone, text: 'Phone intake call: confirms fit, sets expectations (this is a gate, not a formality)' },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <step.icon className="w-4 h-4 text-edupaws-sage mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600 font-body">{step.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Tiers */}
          <div className="space-y-4">
            {BEHAVIOR_ASSESSMENT_TIERS.map(tier => (
              <div key={tier.tier} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-edupaws-navy/5 px-4 py-3 flex items-center justify-between">
                  <div>
                    <span className="font-body font-bold text-sm text-edupaws-navy">{tier.label}</span>
                    <span className="text-xs text-gray-500 font-body ml-2">{tier.range}</span>
                  </div>
                  <span className="font-heading font-bold text-lg text-edupaws-navy">
                    ${tier.price.toLocaleString()}
                    {tier.tier === 'travel' && <span className="text-xs text-gray-400 font-body ml-1">+ expenses</span>}
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  {tier.steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-edupaws-sage/20 text-edupaws-sage flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span className="text-xs text-gray-600 font-body">{step}</span>
                    </div>
                  ))}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400 font-body italic">{tier.scope}</span>
                  </div>
                  {tier.note && (
                    <p className="text-xs text-amber-600 font-body font-medium mt-1">{tier.note}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Support Terms */}
          <div className="bg-edupaws-sage/5 rounded-xl p-4 border border-edupaws-sage/20">
            <h4 className="font-body font-bold text-sm text-edupaws-navy mb-2">Support Terms (All Versions)</h4>
            <ul className="space-y-1 text-xs text-gray-600 font-body">
              <li>• Support opens at assessment, closes at final implementation session</li>
              <li>• Text and email only, no unscheduled calls</li>
              <li>• Weekdays only, responses within one business day</li>
              <li>• For plan questions only. New behaviors book as a session</li>
              <li>• Ends automatically at closing session, no rollover</li>
            </ul>
          </div>

          {/* After the Program */}
          <div>
            <h4 className="font-body font-bold text-sm text-edupaws-navy mb-2">After the Program</h4>
            <ul className="space-y-1 text-xs text-gray-600 font-body">
              <li>• Virtual Follow-Up, $250 for check-ins and plan adjustments</li>
              <li>• In-Person Follow-Up, $350 when hands-on is needed</li>
              <li>• Edu Paws subscription for ongoing structure</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN COMPONENT ====================

export default function Sessions() {
  const { user, activeDog } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [format, setFormat] = useState('virtual');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showBADetail, setShowBADetail] = useState(false);
  const [form, setForm] = useState({
    dog_id: '',
    dog_name: '',
    dog_age_months: '',
    preferred_date: '',
    preferred_time: '',
    zip_code: '',
    address: '',
    notes: '',
    // Inquiry fields for BA / Travel
    phone: '',
    history_notes: '',
  });

  useEffect(() => { if (user) fetchData(); }, [user]);

  useEffect(() => {
    if (activeDog) {
      setForm(f => ({ ...f, dog_id: activeDog.id, dog_name: activeDog.name }));
    }
  }, [activeDog]);

  const fetchData = async () => {
    try {
      const [sessionsRes, dogsRes] = await Promise.all([
        axios.get(`${API}/sessions/my?user_id=${user.id}`),
        axios.get(`${API}/dogs/owner/${user.id}`)
      ]);
      setSessions(sessionsRes.data);
      setDogs(dogsRes.data);
      if (dogsRes.data.length > 0 && !form.dog_id) {
        setForm(f => ({ ...f, dog_id: dogsRes.data[0].id, dog_name: dogsRes.data[0].name }));
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDogChange = (dogId) => {
    const dog = dogs.find(d => d.id === dogId);
    setForm({ ...form, dog_id: dogId, dog_name: dog?.name || '' });
  };

  const handleFormatChange = (f) => {
    setFormat(f);
    setSelectedSession(null);
  };

  const handleSessionSelect = (session) => {
    // Check puppy age restriction
    if (session.maxAge && form.dog_age_months && parseInt(form.dog_age_months) >= session.maxAge) {
      toast.error(`Puppy Consultation is limited to dogs under ${session.maxAge} months. Please book 1-on-1 Training instead.`);
      return;
    }
    setSelectedSession(session);
  };

  const currentSessions = useMemo(() => {
    if (format === 'virtual') return VIRTUAL_SESSIONS;
    if (format === 'local') return LOCAL_SESSIONS;
    return TRAVEL_SESSIONS;
  }, [format]);

  const isInquiryMode = selectedSession?.isInquiry || format === 'travel';
  const needsExtendedFee = format === 'local' && !selectedSession?.isProgram;

  // Calculate final price with extended zone fee
  const getFinalPrice = () => {
    if (!selectedSession) return 0;
    return selectedSession.price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.dog_id || !form.preferred_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate puppy age for puppy consultations
    if (selectedSession?.maxAge && form.dog_age_months) {
      if (parseInt(form.dog_age_months) >= selectedSession.maxAge) {
        toast.error(`This session is for dogs under ${selectedSession.maxAge} months. Please select 1-on-1 Training.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const modeLabel = format === 'virtual' ? 'Virtual' : format === 'local' ? 'In Person, Local' : 'In Person, Travel';
      const sessionNotes = [
        `[${modeLabel}]`,
        form.zip_code ? `ZIP: ${form.zip_code}` : '',
        form.dog_age_months ? `Dog age: ${form.dog_age_months} months` : '',
        form.phone ? `Phone: ${form.phone}` : '',
        isInquiryMode ? '[INQUIRY: Phone intake required before confirmation]' : '',
        form.history_notes ? `History: ${form.history_notes}` : '',
        form.notes,
      ].filter(Boolean).join(' | ');

      await axios.post(`${API}/sessions/request?user_id=${user.id}`, {
        dog_id: form.dog_id,
        dog_name: form.dog_name,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time || 'TBD',
        session_type: selectedSession.value,
        session_mode: format,
        notes: sessionNotes,
      });

      if (isInquiryMode) {
        toast.success('Inquiry submitted! Stephanie will reach out to schedule a phone intake call.');
      } else {
        toast.success('Session request submitted! You\'ll receive confirmation shortly.');
      }

      setForm(f => ({
        ...f,
        preferred_date: '', preferred_time: '', notes: '', zip_code: '', phone: '', history_notes: '', dog_age_months: '',
      }));
      setSelectedSession(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent" />
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold text-edupaws-navy tracking-wide" data-testid="sessions-title">
          Private Sessions
        </h1>
        <p className="text-gray-500 font-body text-base mt-2 max-w-2xl">
          One-on-one sessions with <strong>Stephanie</strong> from the Dog Psychology Center,
          Creator of True Nature, Co-Founder of Edu Paws and SVC Pack Walk.
          These are not sessions with a trainer on a roster. This is one-on-one time with the person who built the curriculum you're training with.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Left Column: Booking */}
        <div className="lg:col-span-3 space-y-6">

          {/* Format Selector */}
          <div data-testid="format-selector">
            <Label className="font-body font-bold text-sm text-edupaws-navy mb-3 block">Session Format</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FORMAT_TABS.map(tab => {
                const isActive = format === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    data-testid={`format-${tab.id}`}
                    onClick={() => handleFormatChange(tab.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      isActive
                        ? 'border-edupaws-sage bg-edupaws-sage/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className={`w-5 h-5 mb-2 ${isActive ? 'text-edupaws-sage' : 'text-gray-400'}`} />
                    <div className="font-body font-bold text-sm text-edupaws-navy">{tab.label}</div>
                    <div className="text-[11px] text-gray-400 font-body mt-0.5">{tab.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Travel Tier Notice */}
          {format === 'travel' && (
            <div data-testid="travel-notice" className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 font-body space-y-1">
                  <p className="font-bold">Travel bookings are by inquiry only.</p>
                  <p>Travel expenses billed separately at cost. Two-day minimum for out of state.
                  50% deposit at booking, non-refundable within 14 days of travel.
                  Quiz, history form, and phone intake must be completed before travel is booked.</p>
                </div>
              </div>
            </div>
          )}

          {/* In Person Notice */}
          {format === 'local' && (
            <div data-testid="local-notice" className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 font-body space-y-1">
                  <p><strong>Dog Psychology Quiz required</strong> before any in-person visit. No exceptions.</p>
                  <p>25 to 60 miles from Santa Clarita: +$150 travel fee per visit. Beyond 60 miles: Travel tier applies.</p>
                </div>
              </div>
            </div>
          )}

          {/* Session Type Cards */}
          <div>
            <Label className="font-body font-bold text-sm text-edupaws-navy mb-3 block">
              {format === 'travel' ? 'Travel Offerings' : 'Session Type'}
            </Label>
            <div className="space-y-3">
              {currentSessions.map(session => (
                <div key={session.value}>
                  <SessionCard
                    session={session}
                    selected={selectedSession}
                    onSelect={handleSessionSelect}
                    format={format}
                  />
                  {session.isProgram && selectedSession?.value === session.value && (
                    <button
                      data-testid="view-ba-details"
                      onClick={() => setShowBADetail(true)}
                      className="mt-2 ml-4 text-xs text-edupaws-sage font-body font-bold hover:underline flex items-center gap-1"
                    >
                      View full program details <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Working Time Note (In Person only) */}
          {format !== 'virtual' && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <h4 className="font-body font-bold text-xs text-edupaws-navy mb-2 uppercase tracking-wider">How Session Time Works</h4>
              <p className="text-xs text-gray-500 font-body leading-relaxed">
                Sessions are broken into working segments with real rest between them. Session lengths are approximate.
                every dog is different, and Stephanie determines in the moment how long a dog can work productively.
                On-site time includes dog working segments, owner instruction, observation of the household dynamic,
                and debrief with homework setup. The dog is the symptom and the human is the foundation.
              </p>
            </div>
          )}

          {/* Booking / Inquiry Form */}
          {selectedSession && (
            <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl" data-testid="booking-form">
              <h3 className="font-heading text-lg text-edupaws-navy mb-4 flex items-center gap-2">
                {isInquiryMode ? (
                  <><Phone className="w-5 h-5 text-edupaws-sage" /> Submit Inquiry</>
                ) : (
                  <><Calendar className="w-5 h-5 text-edupaws-sage" /> Book Session</>
                )}
              </h3>

              {isInquiryMode && (
                <div className="bg-edupaws-sage/10 rounded-lg p-3 mb-4 text-xs text-edupaws-navy font-body">
                  <strong>This is an inquiry, not an instant booking.</strong> Stephanie will review your submission
                  and reach out to schedule a phone intake call before confirming.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Dog Selection */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Which dog?</Label>
                    <select
                      data-testid="session-dog-select"
                      value={form.dog_id}
                      onChange={(e) => handleDogChange(e.target.value)}
                      className="w-full h-11 rounded-xl border-2 border-gray-200 px-3 font-body text-sm focus:outline-none focus:border-edupaws-sage"
                    >
                      {dogs.map(dog => (
                        <option key={dog.id} value={dog.id}>{dog.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Dog's Age (months)</Label>
                    <Input
                      data-testid="dog-age-input"
                      type="number"
                      min="1"
                      max="240"
                      value={form.dog_age_months}
                      onChange={(e) => {
                        setForm({ ...form, dog_age_months: e.target.value });
                        // Auto-redirect if over 8 months on puppy consultation
                        if (selectedSession?.maxAge && parseInt(e.target.value) >= selectedSession.maxAge) {
                          toast.info('Dogs 8+ months should book 1-on-1 Training instead of Puppy Consultation.');
                        }
                      }}
                      placeholder="e.g. 6"
                      className="h-11 rounded-xl border-2 border-gray-200 font-body"
                    />
                  </div>
                </div>

                {/* ZIP / Address for In Person */}
                {format !== 'virtual' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="font-body font-bold text-sm">ZIP Code</Label>
                      <Input
                        data-testid="zip-input"
                        value={form.zip_code}
                        onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                        placeholder="e.g. 91350"
                        className="h-11 rounded-xl border-2 border-gray-200 font-body"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="font-body font-bold text-sm">City / Address</Label>
                      <Input
                        data-testid="address-input"
                        value={form.address}
                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                        placeholder="Santa Clarita, CA"
                        className="h-11 rounded-xl border-2 border-gray-200 font-body"
                      />
                    </div>
                  </div>
                )}

                {/* Phone (for inquiries) */}
                {isInquiryMode && (
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Phone Number (for intake call)</Label>
                    <Input
                      data-testid="phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      required
                      className="h-11 rounded-xl border-2 border-gray-200 font-body"
                    />
                  </div>
                )}

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">
                      {isInquiryMode ? 'Preferred Start Date' : 'Preferred Date'}
                    </Label>
                    <Input
                      data-testid="session-date"
                      type="date"
                      value={form.preferred_date}
                      onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                      required
                      className="h-11 rounded-xl border-2 border-gray-200 font-body"
                    />
                  </div>
                  {!isInquiryMode && (
                    <div className="space-y-1.5">
                      <Label className="font-body font-bold text-sm">Preferred Time</Label>
                      <select
                        data-testid="session-time"
                        value={form.preferred_time}
                        onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                        className="w-full h-11 rounded-xl border-2 border-gray-200 px-3 font-body text-sm focus:outline-none focus:border-edupaws-sage"
                      >
                        <option value="">Select time</option>
                        {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* History Notes (for BA inquiries) */}
                {isInquiryMode && (
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Brief History & Goals</Label>
                    <textarea
                      data-testid="history-notes"
                      value={form.history_notes}
                      onChange={(e) => setForm({ ...form, history_notes: e.target.value })}
                      placeholder="Household composition, specific behaviors prompting the call, what you've already tried..."
                      rows={3}
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                    />
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-1.5">
                  <Label className="font-body font-bold text-sm">Additional Notes (optional)</Label>
                  <textarea
                    data-testid="session-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Anything else Stephanie should know?"
                    rows={2}
                    className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                  />
                </div>

                {/* Price Summary */}
                <div className="bg-edupaws-navy/5 rounded-xl p-4" data-testid="price-summary">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-body font-bold text-sm text-edupaws-navy">{selectedSession.label}</div>
                      <div className="text-xs text-gray-400 font-body mt-0.5">
                        {FORMAT_TABS.find(t => t.id === format)?.label} &middot; {selectedSession.duration}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-2xl font-bold text-edupaws-navy">
                        ${getFinalPrice().toLocaleString()}
                      </div>
                      {selectedSession.note && (
                        <div className="text-[10px] text-gray-400 font-body">{selectedSession.note}</div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="submit-session-btn"
                  disabled={submitting}
                  className="w-full bg-edupaws-navy text-white py-3.5 rounded-xl font-body font-bold text-sm
                    shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all
                    disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? 'Submitting...' : isInquiryMode ? 'Submit Inquiry' : `Request Session - $${getFinalPrice().toLocaleString()}`}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Right Column: My Sessions */}
        <div className="lg:col-span-2">
          <h2 className="font-heading text-xl text-edupaws-navy mb-4">My Sessions</h2>
          {sessions.length === 0 ? (
            <div className="bg-white border-2 border-gray-100 p-8 rounded-2xl text-center">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-body text-sm">No sessions yet. Select a format and session type to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map(session => {
                const statusInfo = STATUS_MAP[session.status] || STATUS_MAP.pending;
                const StatusIcon = statusInfo.icon;
                const allTypes = [...VIRTUAL_SESSIONS, ...LOCAL_SESSIONS, ...TRAVEL_SESSIONS];
                const typeLabel = allTypes.find(t => t.value === session.session_type)?.label || session.session_type;
                return (
                  <div key={session.id} className="bg-white border-2 border-gray-100 p-5 rounded-2xl" data-testid={`session-${session.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-edupaws-navy/10 rounded-xl flex items-center justify-center">
                          <PawPrint className="w-5 h-5 text-edupaws-navy" />
                        </div>
                        <div>
                          <h3 className="font-body font-bold text-sm text-edupaws-navy">{session.dog_name}</h3>
                          <p className="text-xs text-gray-500 font-body">{typeLabel}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-body font-bold ${statusInfo.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusInfo.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500 font-body">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {session.preferred_date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {session.preferred_time}</span>
                    </div>
                    {session.notes && (
                      <p className="mt-2 text-xs text-gray-500 font-body bg-gray-50 p-2.5 rounded-lg line-clamp-2">{session.notes}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Behavior Assessment Detail Modal */}
      <BehaviorAssessmentDetail open={showBADetail} onClose={() => setShowBADetail(false)} />
    </OwnerLayout>
  );
}
